import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity, TrendingUp, TrendingDown, BarChart3,
  AlertCircle, Loader, Search, RefreshCw, Zap,
  ChevronDown, ChevronUp, Info, HelpCircle,
  Brain, Target, ShieldCheck, ShieldAlert, ShieldQuestion,
  BookOpen, Lightbulb, ArrowRight, CheckCircle, XCircle, MinusCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

// Popular stocks for quick selection
const POPULAR_STOCKS = [
  { symbol: 'AAPL', name: 'Apple' },
  { symbol: 'MSFT', name: 'Microsoft' },
  { symbol: 'GOOGL', name: 'Google' },
  { symbol: 'AMZN', name: 'Amazon' },
  { symbol: 'TSLA', name: 'Tesla' },
  { symbol: 'NVDA', name: 'NVIDIA' },
  { symbol: 'META', name: 'Meta' },
  { symbol: 'SPY', name: 'S&P 500 ETF' }
];

// Key indicators that provide the most value
const KEY_INDICATORS = [
  // MOMENTUM INDICATORS
  {
    id: 'RSI',
    name: 'RSI (Relative Strength Index)',
    category: 'momentum',
    color: '#f59e0b',
    icon: <Zap size={18} />,
    description: 'Measures if a stock is overbought or oversold',
    howToRead: 'RSI ranges from 0-100. Below 30 = oversold (potential buy), Above 70 = overbought (potential sell), 30-70 = neutral',
    interpretation: (value) => {
      if (value < 30) return { signal: 'OVERSOLD', type: 'bullish', advice: 'Stock may be undervalued - potential buying opportunity', color: '#22c55e' };
      if (value > 70) return { signal: 'OVERBOUGHT', type: 'bearish', advice: 'Stock may be overvalued - consider taking profits', color: '#ef4444' };
      return { signal: 'NEUTRAL', type: 'neutral', advice: 'Stock is fairly valued - no strong signal', color: '#f59e0b' };
    }
  },
  {
    id: 'STOCH',
    name: 'Stochastic Oscillator',
    category: 'momentum',
    color: '#ec4899',
    icon: <Zap size={18} />,
    description: 'Compares closing price to price range',
    howToRead: 'Above 80 = overbought. Below 20 = oversold. Crossovers signal potential reversals',
    interpretation: (data) => {
      if (!data) return { signal: 'NO DATA', type: 'neutral', advice: 'Unable to analyze', color: '#64748b' };
      const slowK = parseFloat(data.SlowK || data['SlowK']);
      if (slowK > 80) return { signal: 'OVERBOUGHT', type: 'bearish', advice: 'May be due for a pullback', color: '#ef4444' };
      if (slowK < 20) return { signal: 'OVERSOLD', type: 'bullish', advice: 'May be due for a bounce', color: '#22c55e' };
      return { signal: 'NEUTRAL', type: 'neutral', advice: 'No extreme reading', color: '#f59e0b' };
    }
  },
  {
    id: 'CCI',
    name: 'CCI (Commodity Channel Index)',
    category: 'momentum',
    color: '#f97316',
    icon: <Zap size={18} />,
    description: 'Identifies cyclical trends and overbought/oversold levels',
    howToRead: 'Above +100 = overbought, Below -100 = oversold. Zero line crossovers can signal trend changes',
    interpretation: (value) => {
      if (value > 100) return { signal: 'OVERBOUGHT', type: 'bearish', advice: 'Price extended above normal range', color: '#ef4444' };
      if (value < -100) return { signal: 'OVERSOLD', type: 'bullish', advice: 'Price compressed below normal range', color: '#22c55e' };
      return { signal: 'NEUTRAL', type: 'neutral', advice: 'Price within normal range', color: '#f59e0b' };
    }
  },
  {
    id: 'WILLR',
    name: 'Williams %R',
    category: 'momentum',
    color: '#fb923c',
    icon: <Zap size={18} />,
    description: 'Momentum indicator similar to Stochastic',
    howToRead: 'Ranges from 0 to -100. Above -20 = overbought, Below -80 = oversold',
    interpretation: (value) => {
      if (value > -20) return { signal: 'OVERBOUGHT', type: 'bearish', advice: 'Near recent highs - potential pullback', color: '#ef4444' };
      if (value < -80) return { signal: 'OVERSOLD', type: 'bullish', advice: 'Near recent lows - potential bounce', color: '#22c55e' };
      return { signal: 'NEUTRAL', type: 'neutral', advice: 'Mid-range momentum', color: '#f59e0b' };
    }
  },
  {
    id: 'MOM',
    name: 'Momentum',
    category: 'momentum',
    color: '#fbbf24',
    icon: <Zap size={18} />,
    description: 'Measures rate of price change',
    howToRead: 'Positive = upward momentum, Negative = downward momentum. Zero line crossovers are key signals',
    interpretation: (value) => {
      if (value > 5) return { signal: 'STRONG UP', type: 'bullish', advice: 'Strong upward momentum', color: '#22c55e' };
      if (value < -5) return { signal: 'STRONG DOWN', type: 'bearish', advice: 'Strong downward momentum', color: '#ef4444' };
      return { signal: 'WEAK', type: 'neutral', advice: 'Momentum is weak', color: '#f59e0b' };
    }
  },
  {
    id: 'ROC',
    name: 'Rate of Change',
    category: 'momentum',
    color: '#fcd34d',
    icon: <Zap size={18} />,
    description: 'Percentage change in price over time',
    howToRead: 'Positive % = price rising, Negative % = price falling. Extreme values may signal reversals',
    interpretation: (value) => {
      if (value > 10) return { signal: 'STRONG GAIN', type: 'bullish', advice: 'Price rising significantly', color: '#22c55e' };
      if (value < -10) return { signal: 'STRONG LOSS', type: 'bearish', advice: 'Price falling significantly', color: '#ef4444' };
      return { signal: 'MODERATE', type: 'neutral', advice: 'Normal price movement', color: '#f59e0b' };
    }
  },
  {
    id: 'MFI',
    name: 'Money Flow Index',
    category: 'momentum',
    color: '#facc15',
    icon: <Zap size={18} />,
    description: 'Volume-weighted RSI',
    howToRead: 'Like RSI but includes volume. Below 20 = oversold, Above 80 = overbought',
    interpretation: (value) => {
      if (value < 20) return { signal: 'OVERSOLD', type: 'bullish', advice: 'Heavy selling with volume - potential reversal', color: '#22c55e' };
      if (value > 80) return { signal: 'OVERBOUGHT', type: 'bearish', advice: 'Heavy buying with volume - potential top', color: '#ef4444' };
      return { signal: 'NEUTRAL', type: 'neutral', advice: 'Normal money flow', color: '#f59e0b' };
    }
  },
  
  // TREND INDICATORS
  {
    id: 'MACD',
    name: 'MACD',
    category: 'trend',
    color: '#22c55e',
    icon: <TrendingUp size={18} />,
    description: 'Shows momentum and trend direction',
    howToRead: 'When MACD line crosses above Signal line = bullish. When MACD crosses below Signal = bearish',
    interpretation: (data) => {
      if (!data || !data.MACD || !data.MACD_Signal) return { signal: 'NO DATA', type: 'neutral', advice: 'Unable to analyze', color: '#64748b' };
      const macd = parseFloat(data.MACD);
      const signal = parseFloat(data.MACD_Signal);
      if (macd > signal) return { signal: 'BULLISH', type: 'bullish', advice: 'Upward momentum - trend is positive', color: '#22c55e' };
      return { signal: 'BEARISH', type: 'bearish', advice: 'Downward momentum - trend is negative', color: '#ef4444' };
    }
  },
  {
    id: 'SMA',
    name: 'SMA (Simple Moving Average)',
    category: 'trend',
    color: '#10b981',
    icon: <TrendingUp size={18} />,
    description: 'Average price over a period',
    howToRead: 'Price above SMA = bullish trend. Price below SMA = bearish trend. SMA crossovers signal trend changes',
    interpretation: (value, price) => {
      if (!value || !price) return { signal: 'TRACKING', type: 'neutral', advice: 'Monitoring trend', color: '#10b981' };
      if (price > value) return { signal: 'ABOVE SMA', type: 'bullish', advice: 'Price above average - uptrend', color: '#22c55e' };
      return { signal: 'BELOW SMA', type: 'bearish', advice: 'Price below average - downtrend', color: '#ef4444' };
    }
  },
  {
    id: 'EMA',
    name: 'EMA (Exponential Moving Average)',
    category: 'trend',
    color: '#34d399',
    icon: <TrendingUp size={18} />,
    description: 'Weighted average giving more weight to recent prices',
    howToRead: 'More responsive than SMA. Price above EMA = bullish, below = bearish',
    interpretation: (value, price) => {
      if (!value || !price) return { signal: 'TRACKING', type: 'neutral', advice: 'Monitoring trend', color: '#34d399' };
      if (price > value) return { signal: 'ABOVE EMA', type: 'bullish', advice: 'Short-term bullish bias', color: '#22c55e' };
      return { signal: 'BELOW EMA', type: 'bearish', advice: 'Short-term bearish bias', color: '#ef4444' };
    }
  },
  {
    id: 'ADX',
    name: 'ADX (Average Directional Index)',
    category: 'trend',
    color: '#06b6d4',
    icon: <Target size={18} />,
    description: 'Measures trend strength (not direction)',
    howToRead: 'ADX > 25 = strong trend. ADX < 20 = weak/no trend. Higher = stronger trend',
    interpretation: (value) => {
      if (value > 50) return { signal: 'VERY STRONG', type: 'neutral', advice: 'Powerful trend in place - trade with the trend', color: '#22c55e' };
      if (value > 25) return { signal: 'STRONG TREND', type: 'neutral', advice: 'Clear trend present - good for trend following', color: '#06b6d4' };
      return { signal: 'WEAK TREND', type: 'neutral', advice: 'No clear trend - range-bound market', color: '#f59e0b' };
    }
  },
  {
    id: 'AROON',
    name: 'Aroon Indicator',
    category: 'trend',
    color: '#0ea5e9',
    icon: <TrendingUp size={18} />,
    description: 'Identifies trend changes and strength',
    howToRead: 'Aroon Up > 70 = strong uptrend. Aroon Down > 70 = strong downtrend. Crossovers signal trend changes',
    interpretation: (data) => {
      if (!data) return { signal: 'NO DATA', type: 'neutral', advice: 'Unable to analyze', color: '#64748b' };
      const up = parseFloat(data['Aroon Up'] || 0);
      const down = parseFloat(data['Aroon Down'] || 0);
      if (up > 70 && up > down) return { signal: 'UPTREND', type: 'bullish', advice: 'Strong uptrend identified', color: '#22c55e' };
      if (down > 70 && down > up) return { signal: 'DOWNTREND', type: 'bearish', advice: 'Strong downtrend identified', color: '#ef4444' };
      return { signal: 'NO TREND', type: 'neutral', advice: 'No clear trend direction', color: '#f59e0b' };
    }
  },
  
  // VOLATILITY INDICATORS
  {
    id: 'BBANDS',
    name: 'Bollinger Bands',
    category: 'volatility',
    color: '#8b5cf6',
    icon: <Activity size={18} />,
    description: 'Shows volatility and potential price reversals',
    howToRead: 'Price near upper band = potentially overbought. Price near lower band = potentially oversold. Bands widening = increasing volatility',
    interpretation: (data, price) => {
      if (!data) return { signal: 'NO DATA', type: 'neutral', advice: 'Unable to analyze', color: '#64748b' };
      const upper = parseFloat(data['Real Upper Band']);
      const lower = parseFloat(data['Real Lower Band']);
      if (price > upper) return { signal: 'ABOVE UPPER', type: 'bearish', advice: 'Price extended - may pull back to middle', color: '#ef4444' };
      if (price < lower) return { signal: 'BELOW LOWER', type: 'bullish', advice: 'Price compressed - may bounce back up', color: '#22c55e' };
      return { signal: 'WITHIN BANDS', type: 'neutral', advice: 'Price in normal range', color: '#f59e0b' };
    }
  },
  {
    id: 'ATR',
    name: 'ATR (Average True Range)',
    category: 'volatility',
    color: '#a78bfa',
    icon: <Activity size={18} />,
    description: 'Measures market volatility',
    howToRead: 'Higher ATR = more volatile. Lower ATR = less volatile. Use for position sizing and stop losses',
    interpretation: (value, avgValue) => {
      return { signal: 'VOLATILITY', type: 'neutral', advice: `Current volatility: $${parseFloat(value).toFixed(2)} average range`, color: '#a78bfa' };
    }
  },
  {
    id: 'NATR',
    name: 'Normalized ATR',
    category: 'volatility',
    color: '#c4b5fd',
    icon: <Activity size={18} />,
    description: 'ATR as a percentage of price',
    howToRead: 'Higher % = more volatile relative to price. Useful for comparing volatility across different priced stocks',
    interpretation: (value) => {
      if (value > 5) return { signal: 'HIGH VOLATILITY', type: 'neutral', advice: 'Stock is very volatile - use caution', color: '#ef4444' };
      if (value < 1) return { signal: 'LOW VOLATILITY', type: 'neutral', advice: 'Stock is relatively stable', color: '#22c55e' };
      return { signal: 'MODERATE', type: 'neutral', advice: 'Normal volatility levels', color: '#f59e0b' };
    }
  },
  
  // VOLUME INDICATORS
  {
    id: 'OBV',
    name: 'OBV (On Balance Volume)',
    category: 'volume',
    color: '#14b8a6',
    icon: <BarChart3 size={18} />,
    description: 'Uses volume to predict price changes',
    howToRead: 'Rising OBV = accumulation (buying). Falling OBV = distribution (selling). Confirms price trends',
    interpretation: (data, prevData) => {
      if (!data || !prevData) return { signal: 'TRACKING', type: 'neutral', advice: 'Volume flow being monitored', color: '#14b8a6' };
      const current = parseFloat(data.OBV);
      const prev = parseFloat(prevData.OBV);
      if (current > prev) return { signal: 'ACCUMULATION', type: 'bullish', advice: 'Smart money buying - bullish sign', color: '#22c55e' };
      return { signal: 'DISTRIBUTION', type: 'bearish', advice: 'Smart money selling - bearish sign', color: '#ef4444' };
    }
  },
  {
    id: 'AD',
    name: 'Accumulation/Distribution',
    category: 'volume',
    color: '#2dd4bf',
    icon: <BarChart3 size={18} />,
    description: 'Measures cumulative money flow',
    howToRead: 'Rising A/D = accumulation. Falling A/D = distribution. Divergences from price signal potential reversals',
    interpretation: (data, prevData) => {
      if (!data || !prevData) return { signal: 'TRACKING', type: 'neutral', advice: 'Monitoring accumulation/distribution', color: '#2dd4bf' };
      const current = parseFloat(data['Chaikin A/D'] || data.AD || 0);
      const prev = parseFloat(prevData['Chaikin A/D'] || prevData.AD || 0);
      if (current > prev) return { signal: 'ACCUMULATION', type: 'bullish', advice: 'Money flowing in', color: '#22c55e' };
      return { signal: 'DISTRIBUTION', type: 'bearish', advice: 'Money flowing out', color: '#ef4444' };
    }
  },
  {
    id: 'ADOSC',
    name: 'Chaikin A/D Oscillator',
    category: 'volume',
    color: '#5eead4',
    icon: <BarChart3 size={18} />,
    description: 'Momentum of accumulation/distribution',
    howToRead: 'Positive = bullish volume momentum. Negative = bearish volume momentum. Zero crossovers are signals',
    interpretation: (value) => {
      if (value > 0) return { signal: 'BULLISH VOLUME', type: 'bullish', advice: 'Positive volume momentum', color: '#22c55e' };
      return { signal: 'BEARISH VOLUME', type: 'bearish', advice: 'Negative volume momentum', color: '#ef4444' };
    }
  }
];

const TechnicalAnalysisDashboard = () => {
  const [symbol, setSymbol] = useState('AAPL');
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [indicators, setIndicators] = useState({});
  const [currentPrice, setCurrentPrice] = useState(null);
  const [companyName, setCompanyName] = useState('Apple Inc.');
  const [overallSignal, setOverallSignal] = useState(null);
  const [showEducation, setShowEducation] = useState(false);
  const [expandedIndicator, setExpandedIndicator] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [failedIndicators, setFailedIndicators] = useState([]);

  // Load AAPL on mount
  useEffect(() => {
    fetchIndicators('AAPL');
  }, []);

  // Search for stocks
  const searchStocks = async (query) => {
    if (!query || query.length < 1) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await fetch(`${API_URL}/stocks/search?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      setSearchResults(Array.isArray(data) ? data.slice(0, 8) : []);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.length >= 1) {
        searchStocks(searchInput);
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch stock price
  const fetchPrice = async (sym) => {
    try {
      const response = await fetch(`${API_URL}/stocks/${sym}`);
      const data = await response.json();
      setCurrentPrice(data.currentPrice);
      setCompanyName(data.name || sym);
    } catch (err) {
      console.error('Price fetch error:', err);
    }
  };

  // Fetch all key indicators with staggered requests to avoid burst limits
  // Alpha Vantage has a burst limit of 10 requests/second even for premium
  const fetchIndicators = useCallback(async (sym) => {
    setLoading(true);
    setError(null);
    setIndicators({});
    setFailedIndicators([]);
    
    try {
      // Fetch price first
      await fetchPrice(sym);
      
      const indicatorData = {};
      const failed = [];
      
      // Fetch indicators one by one with small delay to avoid burst rate limits
      for (let i = 0; i < KEY_INDICATORS.length; i++) {
        const indicator = KEY_INDICATORS[i];
        
        try {
          const response = await fetch(
            `${API_URL}/technical/${sym}/${indicator.id.toLowerCase()}?interval=daily`
          );
          const data = await response.json();
          
          if (data.success !== false && data.data && data.data.length > 0) {
            indicatorData[indicator.id] = {
              current: data.data[0],
              previous: data.data[1] || null,
              history: data.data.slice(0, 10)
            };
          } else {
            failed.push(indicator.id);
          }
        } catch (err) {
          console.error(`Failed to fetch ${indicator.id}:`, err);
          failed.push(indicator.id);
        }
        
        // Small delay between requests (120ms = ~8 requests/second, safe for premium)
        if (i < KEY_INDICATORS.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 120));
        }
      }
      
      setIndicators(indicatorData);
      setFailedIndicators(failed);
      calculateOverallSignal(indicatorData);
      
      console.log(`✅ Loaded ${Object.keys(indicatorData).length}/${KEY_INDICATORS.length} indicators for ${sym}`);
      
      if (Object.keys(indicatorData).length === 0) {
        setError('Unable to load indicators. Please check the symbol and try again.');
      }
      
    } catch (err) {
      console.error('Error fetching indicators:', err);
      setError('Failed to load technical indicators. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Calculate overall signal from ALL indicators
  const calculateOverallSignal = (data) => {
    let bullish = 0;
    let bearish = 0;
    let neutral = 0;
    const signals = [];

    // Helper to add signal
    const addSignal = (name, interpretation, value = null) => {
      signals.push({ 
        name, 
        ...interpretation, 
        value: value !== null ? value : undefined 
      });
      if (interpretation.type === 'bullish') bullish++;
      else if (interpretation.type === 'bearish') bearish++;
      else neutral++;
    };

    // RSI
    if (data.RSI?.current?.RSI) {
      const rsi = parseFloat(data.RSI.current.RSI);
      const interp = KEY_INDICATORS.find(i => i.id === 'RSI').interpretation(rsi);
      addSignal('RSI', interp, rsi.toFixed(1));
    }

    // Stochastic
    if (data.STOCH?.current) {
      const interp = KEY_INDICATORS.find(i => i.id === 'STOCH').interpretation(data.STOCH.current);
      const value = data.STOCH.current.SlowK || data.STOCH.current['SlowK'];
      addSignal('Stochastic', interp, parseFloat(value).toFixed(1));
    }

    // CCI
    if (data.CCI?.current?.CCI) {
      const cci = parseFloat(data.CCI.current.CCI);
      const interp = KEY_INDICATORS.find(i => i.id === 'CCI').interpretation(cci);
      addSignal('CCI', interp, cci.toFixed(1));
    }

    // Williams %R
    if (data.WILLR?.current) {
      const willr = parseFloat(data.WILLR.current.WILLR || data.WILLR.current['WILLR']);
      const interp = KEY_INDICATORS.find(i => i.id === 'WILLR').interpretation(willr);
      addSignal('Williams %R', interp, willr.toFixed(1));
    }

    // MFI
    if (data.MFI?.current?.MFI) {
      const mfi = parseFloat(data.MFI.current.MFI);
      const interp = KEY_INDICATORS.find(i => i.id === 'MFI').interpretation(mfi);
      addSignal('MFI', interp, mfi.toFixed(1));
    }

    // Momentum
    if (data.MOM?.current?.MOM) {
      const mom = parseFloat(data.MOM.current.MOM);
      const interp = KEY_INDICATORS.find(i => i.id === 'MOM').interpretation(mom);
      addSignal('Momentum', interp, mom.toFixed(2));
    }

    // ROC
    if (data.ROC?.current?.ROC) {
      const roc = parseFloat(data.ROC.current.ROC);
      const interp = KEY_INDICATORS.find(i => i.id === 'ROC').interpretation(roc);
      addSignal('ROC', interp, roc.toFixed(2));
    }

    // MACD
    if (data.MACD?.current) {
      const interp = KEY_INDICATORS.find(i => i.id === 'MACD').interpretation(data.MACD.current);
      const macdVal = data.MACD.current.MACD ? parseFloat(data.MACD.current.MACD).toFixed(2) : null;
      addSignal('MACD', interp, macdVal);
    }

    // SMA (compare to current price)
    if (data.SMA?.current?.SMA && currentPrice) {
      const sma = parseFloat(data.SMA.current.SMA);
      const interp = KEY_INDICATORS.find(i => i.id === 'SMA').interpretation(sma, currentPrice);
      addSignal('SMA', interp, sma.toFixed(2));
    }

    // EMA (compare to current price)
    if (data.EMA?.current?.EMA && currentPrice) {
      const ema = parseFloat(data.EMA.current.EMA);
      const interp = KEY_INDICATORS.find(i => i.id === 'EMA').interpretation(ema, currentPrice);
      addSignal('EMA', interp, ema.toFixed(2));
    }

    // ADX (trend strength - neutral indicator)
    if (data.ADX?.current?.ADX) {
      const adx = parseFloat(data.ADX.current.ADX);
      const interp = KEY_INDICATORS.find(i => i.id === 'ADX').interpretation(adx);
      addSignal('ADX', interp, adx.toFixed(1));
    }

    // Aroon
    if (data.AROON?.current) {
      const interp = KEY_INDICATORS.find(i => i.id === 'AROON').interpretation(data.AROON.current);
      const aroonUp = data.AROON.current['Aroon Up'];
      addSignal('Aroon', interp, aroonUp ? parseFloat(aroonUp).toFixed(0) : null);
    }

    // Bollinger Bands
    if (data.BBANDS?.current && currentPrice) {
      const interp = KEY_INDICATORS.find(i => i.id === 'BBANDS').interpretation(data.BBANDS.current, currentPrice);
      addSignal('Bollinger', interp);
    }

    // ATR (volatility - neutral)
    if (data.ATR?.current?.ATR) {
      const atr = parseFloat(data.ATR.current.ATR);
      const interp = KEY_INDICATORS.find(i => i.id === 'ATR').interpretation(atr);
      addSignal('ATR', interp, atr.toFixed(2));
    }

    // NATR
    if (data.NATR?.current?.NATR) {
      const natr = parseFloat(data.NATR.current.NATR);
      const interp = KEY_INDICATORS.find(i => i.id === 'NATR').interpretation(natr);
      addSignal('NATR', interp, natr.toFixed(2) + '%');
    }

    // OBV
    if (data.OBV?.current && data.OBV?.previous) {
      const interp = KEY_INDICATORS.find(i => i.id === 'OBV').interpretation(data.OBV.current, data.OBV.previous);
      addSignal('OBV', interp);
    }

    // A/D
    if (data.AD?.current && data.AD?.previous) {
      const interp = KEY_INDICATORS.find(i => i.id === 'AD').interpretation(data.AD.current, data.AD.previous);
      addSignal('A/D', interp);
    }

    // ADOSC
    if (data.ADOSC?.current) {
      const adosc = parseFloat(data.ADOSC.current['Chaikin A/D Oscillator'] || data.ADOSC.current.ADOSC || 0);
      const interp = KEY_INDICATORS.find(i => i.id === 'ADOSC').interpretation(adosc);
      addSignal('Chaikin Osc', interp, adosc.toFixed(0));
    }

    // Calculate overall signal
    const total = bullish + bearish + neutral;
    const bullishPercent = total > 0 ? (bullish / total) * 100 : 0;
    const bearishPercent = total > 0 ? (bearish / total) * 100 : 0;

    let overall = 'NEUTRAL';
    let overallColor = '#f59e0b';
    let overallAdvice = '';
    let overallIcon = <MinusCircle size={24} />;

    // Strong Bullish: >60% bullish signals
    if (bullishPercent >= 60) {
      overall = 'STRONG BUY';
      overallColor = '#22c55e';
      overallAdvice = `${bullish} of ${total} indicators are bullish (${bullishPercent.toFixed(0)}%). Strong upward momentum detected.`;
      overallIcon = <CheckCircle size={24} />;
    }
    // Bullish: >40% bullish and bullish > bearish
    else if (bullishPercent >= 40 && bullish > bearish) {
      overall = 'BULLISH';
      overallColor = '#4ade80';
      overallAdvice = `${bullish} of ${total} indicators are bullish. Moderate upward bias.`;
      overallIcon = <TrendingUp size={24} />;
    }
    // Strong Bearish: >60% bearish signals
    else if (bearishPercent >= 60) {
      overall = 'STRONG SELL';
      overallColor = '#ef4444';
      overallAdvice = `${bearish} of ${total} indicators are bearish (${bearishPercent.toFixed(0)}%). Strong downward pressure detected.`;
      overallIcon = <XCircle size={24} />;
    }
    // Bearish: >40% bearish and bearish > bullish
    else if (bearishPercent >= 40 && bearish > bullish) {
      overall = 'BEARISH';
      overallColor = '#f87171';
      overallAdvice = `${bearish} of ${total} indicators are bearish. Moderate downward bias.`;
      overallIcon = <TrendingDown size={24} />;
    }
    // Neutral
    else {
      overall = 'NEUTRAL';
      overallColor = '#f59e0b';
      overallAdvice = `Mixed signals: ${bullish} bullish, ${bearish} bearish, ${neutral} neutral. No clear direction - wait for confirmation.`;
      overallIcon = <MinusCircle size={24} />;
    }

    setOverallSignal({
      signal: overall,
      color: overallColor,
      advice: overallAdvice,
      icon: overallIcon,
      bullishCount: bullish,
      bearishCount: bearish,
      neutralCount: neutral,
      totalCount: total,
      bullishPercent,
      bearishPercent,
      signals
    });
  };

  // Handle stock selection
  const handleSelectStock = (sym, name) => {
    setSymbol(sym);
    setCompanyName(name || sym);
    setSearchInput('');
    setSearchResults([]);
    setShowEducation(false);
    fetchIndicators(sym);
  };

  // Get signal icon
  const getSignalIcon = (type) => {
    switch (type) {
      case 'bullish': return <TrendingUp size={16} color="#22c55e" />;
      case 'bearish': return <TrendingDown size={16} color="#ef4444" />;
      default: return <MinusCircle size={16} color="#f59e0b" />;
    }
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 0.5rem' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #0f766e 50%, #14b8a6 100%)',
        borderRadius: '1rem',
        padding: '1.25rem',
        marginBottom: '1.5rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-10%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Title - Stack on mobile */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.75rem', 
            marginBottom: '1rem',
            flexWrap: 'wrap'
          }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '0.75rem',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Brain size={24} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <h1 style={{ 
                fontSize: 'clamp(1.1rem, 4vw, 1.5rem)', 
                fontWeight: '800', 
                color: 'white',
                margin: 0,
                lineHeight: 1.2
              }}>
                Technical Analysis
              </h1>
              <p style={{ 
                color: 'rgba(255, 255, 255, 0.8)', 
                margin: 0, 
                fontSize: 'clamp(0.75rem, 2.5vw, 0.9rem)' 
              }}>
                AI-powered signals • Educational
              </p>
            </div>
          </div>

          {/* Search Bar - Full width on mobile */}
          <div style={{ position: 'relative', width: '100%', maxWidth: '500px' }}>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'rgba(255, 255, 255, 0.6)'
              }} />
              <input
                type="text"
                placeholder="Search stock..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value.toUpperCase())}
                style={{
                  width: '100%',
                  padding: '0.75rem 2.5rem 0.75rem 2.5rem',
                  background: 'rgba(255, 255, 255, 0.15)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '0.75rem',
                  color: 'white',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
              {searching && (
                <Loader size={18} style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  animation: 'spin 1s linear infinite'
                }} />
              )}
            </div>

            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '0.5rem',
                marginTop: '0.5rem',
                zIndex: 100,
                maxHeight: '300px',
                overflow: 'auto'
              }}>
                {searchResults.map((result, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleSelectStock(result.symbol, result.name)}
                    style={{
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      borderBottom: idx < searchResults.length - 1 ? '1px solid #334155' : 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#334155'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div>
                      <div style={{ fontWeight: '600', color: '#e2e8f0' }}>{result.symbol}</div>
                      <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{result.name}</div>
                    </div>
                    <ArrowRight size={16} color="#64748b" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Popular Stocks - Horizontal scroll on mobile */}
          <div style={{ marginTop: '1rem' }}>
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
              Quick select:
            </p>
            <div style={{ 
              display: 'flex', 
              gap: '0.4rem', 
              flexWrap: 'nowrap',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              paddingBottom: '0.5rem',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}>
              {POPULAR_STOCKS.map((stock) => (
                <button
                  key={stock.symbol}
                  onClick={() => handleSelectStock(stock.symbol, stock.name)}
                  style={{
                    padding: '0.4rem 0.75rem',
                    background: symbol === stock.symbol ? 'white' : 'rgba(255, 255, 255, 0.15)',
                    border: 'none',
                    borderRadius: '0.5rem',
                    color: symbol === stock.symbol ? '#0f766e' : 'white',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}
                >
                  {stock.symbol}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Education Section (shown when no stock selected) */}
      {showEducation && !symbol && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)',
          border: '1px solid rgba(99, 102, 241, 0.2)',
          borderRadius: '1rem',
          padding: '2rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <BookOpen size={24} color="#818cf8" />
            <h2 style={{ color: '#e2e8f0', margin: 0, fontSize: '1.25rem' }}>
              📚 What is Technical Analysis?
            </h2>
          </div>
          
          <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1.5rem' }}>
            Technical analysis uses historical price and volume data to predict future price movements. 
            Instead of looking at a company's financials, technical analysts study <strong>charts and indicators</strong> to 
            identify patterns and trends.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1rem'
          }}>
            <EducationCard
              icon={<Zap size={20} color="#f59e0b" />}
              title="Momentum Indicators"
              description="Show if a stock is overbought or oversold. Examples: RSI, Stochastic"
              color="#f59e0b"
            />
            <EducationCard
              icon={<TrendingUp size={20} color="#22c55e" />}
              title="Trend Indicators"
              description="Show the direction and strength of a trend. Examples: MACD, Moving Averages"
              color="#22c55e"
            />
            <EducationCard
              icon={<Activity size={20} color="#8b5cf6" />}
              title="Volatility Indicators"
              description="Measure how much prices fluctuate. Examples: Bollinger Bands, ATR"
              color="#8b5cf6"
            />
            <EducationCard
              icon={<BarChart3 size={20} color="#14b8a6" />}
              title="Volume Indicators"
              description="Track buying and selling pressure. Examples: OBV, Volume Profile"
              color="#14b8a6"
            />
          </div>

          <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <Lightbulb size={20} color="#22c55e" />
            <p style={{ color: '#86efac', margin: 0, fontSize: '0.9rem' }}>
              <strong>Get Started:</strong> Search for a stock above or click one of the popular stocks to see its technical analysis!
            </p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{
          background: 'rgba(30, 41, 59, 0.8)',
          borderRadius: '1rem',
          padding: '3rem 2rem',
          textAlign: 'center'
        }}>
          <Loader size={48} color="#14b8a6" style={{ animation: 'spin 1s linear infinite', marginBottom: '1rem' }} />
          <p style={{ color: '#e2e8f0', fontSize: '1.1rem', marginBottom: '0.5rem' }}>Analyzing {symbol}...</p>
          <p style={{ color: '#64748b', fontSize: '0.85rem' }}>
            Loading all 18 technical indicators
          </p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <AlertCircle size={32} color="#ef4444" style={{ marginBottom: '0.5rem' }} />
          <p style={{ color: '#f87171', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Results */}
      {symbol && !loading && Object.keys(indicators).length > 0 && (
        <>
          {/* Stock Header */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.8)',
            borderRadius: '1rem',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <h2 style={{ color: '#e2e8f0', margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>
                  {symbol}
                </h2>
                {currentPrice && (
                  <span style={{
                    background: 'rgba(20, 184, 166, 0.2)',
                    color: '#5eead4',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '0.5rem',
                    fontSize: '1.1rem',
                    fontWeight: '600'
                  }}>
                    ${currentPrice.toFixed(2)}
                  </span>
                )}
              </div>
              <p style={{ color: '#94a3b8', margin: '0.25rem 0 0', fontSize: '0.9rem' }}>{companyName}</p>
            </div>
            
            <button
              onClick={() => fetchIndicators(symbol)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.25rem',
                background: '#0f766e',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600'
              }}
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>

          {/* Overall Signal */}
          {overallSignal && (
            <div style={{
              background: `linear-gradient(135deg, ${overallSignal.color}15 0%, ${overallSignal.color}05 100%)`,
              border: `2px solid ${overallSignal.color}50`,
              borderRadius: '1rem',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              {/* Main Signal Display */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    background: `${overallSignal.color}30`,
                    borderRadius: '1rem',
                    padding: '1rem',
                    color: overallSignal.color
                  }}>
                    {overallSignal.icon}
                  </div>
                  <div>
                    <h3 style={{ color: '#e2e8f0', margin: 0, fontSize: '1rem' }}>Overall Technical Signal</h3>
                    <div style={{ 
                      fontSize: '1.75rem', 
                      fontWeight: '800', 
                      color: overallSignal.color,
                      marginTop: '0.25rem'
                    }}>
                      {overallSignal.signal}
                    </div>
                  </div>
                </div>
                
                {/* Signal Breakdown Box */}
                <div style={{
                  background: 'rgba(15, 23, 42, 0.6)',
                  borderRadius: '0.75rem',
                  padding: '1rem 1.25rem',
                  minWidth: '200px'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.75rem', textAlign: 'center' }}>
                    Signal Breakdown ({overallSignal.totalCount || 0} indicators)
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-around', gap: '1rem' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#22c55e', fontSize: '1.5rem', fontWeight: '700' }}>
                        {overallSignal.bullishCount || 0}
                      </div>
                      <div style={{ color: '#86efac', fontSize: '0.7rem' }}>Bullish</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#f59e0b', fontSize: '1.5rem', fontWeight: '700' }}>
                        {overallSignal.neutralCount || 0}
                      </div>
                      <div style={{ color: '#fcd34d', fontSize: '0.7rem' }}>Neutral</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#ef4444', fontSize: '1.5rem', fontWeight: '700' }}>
                        {overallSignal.bearishCount || 0}
                      </div>
                      <div style={{ color: '#fca5a5', fontSize: '0.7rem' }}>Bearish</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Visual Progress Bar */}
              <div style={{ 
                marginTop: '1.25rem',
                padding: '1rem',
                background: 'rgba(15, 23, 42, 0.4)',
                borderRadius: '0.75rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#22c55e', fontSize: '0.8rem', fontWeight: '600' }}>
                    Bullish {overallSignal.bullishPercent?.toFixed(0) || 0}%
                  </span>
                  <span style={{ color: '#ef4444', fontSize: '0.8rem', fontWeight: '600' }}>
                    Bearish {overallSignal.bearishPercent?.toFixed(0) || 0}%
                  </span>
                </div>
                <div style={{
                  height: '12px',
                  background: '#1e293b',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  display: 'flex'
                }}>
                  <div style={{
                    width: `${overallSignal.bullishPercent || 0}%`,
                    background: 'linear-gradient(90deg, #22c55e, #4ade80)',
                    transition: 'width 0.5s ease'
                  }} />
                  <div style={{
                    width: `${100 - (overallSignal.bullishPercent || 0) - (overallSignal.bearishPercent || 0)}%`,
                    background: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
                    transition: 'width 0.5s ease'
                  }} />
                  <div style={{
                    width: `${overallSignal.bearishPercent || 0}%`,
                    background: 'linear-gradient(90deg, #f87171, #ef4444)',
                    transition: 'width 0.5s ease'
                  }} />
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: '1.5rem', 
                  marginTop: '0.5rem',
                  fontSize: '0.7rem',
                  color: '#64748b'
                }}>
                  <span>🟢 Bullish</span>
                  <span>🟡 Neutral</span>
                  <span>🔴 Bearish</span>
                </div>
              </div>
              
              {/* Advice/Explanation */}
              <div style={{ 
                marginTop: '1rem',
                padding: '1rem',
                background: 'rgba(15, 23, 42, 0.4)',
                borderRadius: '0.5rem',
                borderLeft: `4px solid ${overallSignal.color}`
              }}>
                <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                  📊 Analysis Summary
                </div>
                <p style={{ color: '#e2e8f0', margin: 0, fontSize: '0.95rem', lineHeight: '1.6' }}>
                  {overallSignal.advice}
                </p>
              </div>

              {/* How We Calculate - Expandable */}
              <details style={{ marginTop: '1rem' }}>
                <summary style={{ 
                  color: '#94a3b8', 
                  fontSize: '0.8rem', 
                  cursor: 'pointer',
                  padding: '0.5rem',
                  background: 'rgba(15, 23, 42, 0.3)',
                  borderRadius: '0.5rem',
                  userSelect: 'none'
                }}>
                  ℹ️ How is this calculated?
                </summary>
                <div style={{ 
                  marginTop: '0.5rem',
                  padding: '1rem',
                  background: 'rgba(15, 23, 42, 0.3)',
                  borderRadius: '0.5rem',
                  fontSize: '0.85rem',
                  color: '#94a3b8',
                  lineHeight: '1.7'
                }}>
                  <p style={{ margin: '0 0 0.75rem' }}>
                    We analyze <strong style={{ color: '#e2e8f0' }}>{overallSignal.totalCount || 0} technical indicators</strong> across 4 categories:
                  </p>
                  <ul style={{ margin: '0 0 0.75rem', paddingLeft: '1.25rem' }}>
                    <li><span style={{ color: '#f59e0b' }}>Momentum</span>: RSI, Stochastic, CCI, Williams %R, MFI, Momentum, ROC</li>
                    <li><span style={{ color: '#22c55e' }}>Trend</span>: MACD, SMA, EMA, ADX, Aroon</li>
                    <li><span style={{ color: '#8b5cf6' }}>Volatility</span>: Bollinger Bands, ATR, NATR</li>
                    <li><span style={{ color: '#14b8a6' }}>Volume</span>: OBV, A/D Line, Chaikin Oscillator</li>
                  </ul>
                  <p style={{ margin: '0 0 0.5rem' }}><strong style={{ color: '#e2e8f0' }}>Signal Thresholds:</strong></p>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                    <li><span style={{ color: '#22c55e' }}>STRONG BUY</span>: 60%+ indicators are bullish</li>
                    <li><span style={{ color: '#4ade80' }}>BULLISH</span>: 40%+ bullish and bullish {'>'} bearish</li>
                    <li><span style={{ color: '#f59e0b' }}>NEUTRAL</span>: Mixed signals, no clear majority</li>
                    <li><span style={{ color: '#f87171' }}>BEARISH</span>: 40%+ bearish and bearish {'>'} bullish</li>
                    <li><span style={{ color: '#ef4444' }}>STRONG SELL</span>: 60%+ indicators are bearish</li>
                  </ul>
                </div>
              </details>
            </div>
          )}

          {/* Individual Indicators */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.5rem',
              marginBottom: '1rem'
            }}>
              <h3 style={{ 
                color: '#e2e8f0', 
                fontSize: 'clamp(0.95rem, 3vw, 1.1rem)', 
                fontWeight: '600',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Activity size={18} color="#14b8a6" />
                Indicators
                <span style={{
                  background: 'rgba(34, 197, 94, 0.2)',
                  color: '#22c55e',
                  padding: '0.2rem 0.5rem',
                  borderRadius: '0.375rem',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  {Object.keys(indicators).length}/{KEY_INDICATORS.length}
                </span>
              </h3>
              
              {failedIndicators.length > 0 && (
                <span style={{
                  background: 'rgba(251, 191, 36, 0.2)',
                  color: '#fbbf24',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.375rem',
                  fontSize: '0.7rem'
                }}>
                  {failedIndicators.length} rate limited
                </span>
              )}
            </div>

            {/* Category Filter Tabs - Horizontal scroll on mobile */}
            <div style={{
              display: 'flex',
              gap: '0.4rem',
              marginBottom: '1rem',
              flexWrap: 'nowrap',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              paddingBottom: '0.5rem',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}>
              <button
                onClick={() => setActiveCategory('all')}
                style={{
                  padding: '0.4rem 0.75rem',
                  background: activeCategory === 'all' ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(30, 41, 59, 0.8)',
                  border: activeCategory === 'all' ? 'none' : '1px solid #334155',
                  borderRadius: '0.5rem',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: activeCategory === 'all' ? '600' : '400',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                All ({KEY_INDICATORS.length})
              </button>
              <button
                onClick={() => setActiveCategory('momentum')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  padding: '0.4rem 0.75rem',
                  background: activeCategory === 'momentum' ? 'rgba(245, 158, 11, 0.3)' : 'rgba(30, 41, 59, 0.8)',
                  border: activeCategory === 'momentum' ? '1px solid #f59e0b' : '1px solid #334155',
                  borderRadius: '0.5rem',
                  color: activeCategory === 'momentum' ? '#f59e0b' : '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: activeCategory === 'momentum' ? '600' : '400',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                <Zap size={12} />
                Momentum
              </button>
              <button
                onClick={() => setActiveCategory('trend')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  padding: '0.4rem 0.75rem',
                  background: activeCategory === 'trend' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(30, 41, 59, 0.8)',
                  border: activeCategory === 'trend' ? '1px solid #22c55e' : '1px solid #334155',
                  borderRadius: '0.5rem',
                  color: activeCategory === 'trend' ? '#22c55e' : '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: activeCategory === 'trend' ? '600' : '400',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                <TrendingUp size={12} />
                Trend
              </button>
              <button
                onClick={() => setActiveCategory('volatility')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  padding: '0.4rem 0.75rem',
                  background: activeCategory === 'volatility' ? 'rgba(139, 92, 246, 0.3)' : 'rgba(30, 41, 59, 0.8)',
                  border: activeCategory === 'volatility' ? '1px solid #8b5cf6' : '1px solid #334155',
                  borderRadius: '0.5rem',
                  color: activeCategory === 'volatility' ? '#8b5cf6' : '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: activeCategory === 'volatility' ? '600' : '400',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                <Activity size={12} />
                Volatility
              </button>
              <button
                onClick={() => setActiveCategory('volume')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  padding: '0.4rem 0.75rem',
                  background: activeCategory === 'volume' ? 'rgba(20, 184, 166, 0.3)' : 'rgba(30, 41, 59, 0.8)',
                  border: activeCategory === 'volume' ? '1px solid #14b8a6' : '1px solid #334155',
                  borderRadius: '0.5rem',
                  color: activeCategory === 'volume' ? '#14b8a6' : '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: activeCategory === 'volume' ? '600' : '400',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                <BarChart3 size={12} />
                Volume
              </button>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
            gap: '0.75rem'
          }}>
            {KEY_INDICATORS
              .filter(indicator => activeCategory === 'all' || indicator.category === activeCategory)
              .map((indicator) => {
              const data = indicators[indicator.id];
              
              // Show placeholder for indicators that didn't load
              if (!data) {
                // Only show if in current category
                if (activeCategory !== 'all' && indicator.category !== activeCategory) return null;
                
                return (
                  <div
                    key={indicator.id}
                    style={{
                      background: 'rgba(30, 41, 59, 0.5)',
                      border: '1px solid #334155',
                      borderRadius: '0.75rem',
                      padding: '1rem',
                      opacity: 0.6
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{
                        background: `${indicator.color}20`,
                        borderRadius: '0.5rem',
                        padding: '0.5rem',
                        color: indicator.color
                      }}>
                        {indicator.icon}
                      </div>
                      <div>
                        <h4 style={{ color: '#94a3b8', margin: 0, fontSize: '0.85rem' }}>
                          {indicator.name.split(' ')[0]}
                        </h4>
                        <p style={{ color: '#64748b', margin: 0, fontSize: '0.75rem' }}>
                          {failedIndicators.includes(indicator.id) ? 'Rate limited - try again' : 'Loading...'}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              }

              let value, interpretation;
              
              // Extract value based on indicator type
              if (indicator.id === 'RSI') {
                value = parseFloat(data.current.RSI);
                interpretation = indicator.interpretation(value);
              } else if (indicator.id === 'MACD') {
                value = data.current.MACD ? parseFloat(data.current.MACD).toFixed(2) : 'N/A';
                interpretation = indicator.interpretation(data.current);
              } else if (indicator.id === 'BBANDS') {
                value = data.current['Real Middle Band'] ? parseFloat(data.current['Real Middle Band']).toFixed(2) : 'N/A';
                interpretation = indicator.interpretation(data.current, currentPrice);
              } else if (indicator.id === 'ADX') {
                value = parseFloat(data.current.ADX);
                interpretation = indicator.interpretation(value);
              } else if (indicator.id === 'STOCH') {
                value = data.current.SlowK ? parseFloat(data.current.SlowK).toFixed(1) : 'N/A';
                interpretation = indicator.interpretation(data.current);
              } else if (indicator.id === 'OBV') {
                value = data.current.OBV ? (parseFloat(data.current.OBV) / 1000000).toFixed(2) + 'M' : 'N/A';
                interpretation = indicator.interpretation(data.current, data.previous);
              } else if (indicator.id === 'SMA') {
                value = data.current.SMA ? parseFloat(data.current.SMA).toFixed(2) : 'N/A';
                interpretation = indicator.interpretation(parseFloat(data.current.SMA), currentPrice);
              } else if (indicator.id === 'EMA') {
                value = data.current.EMA ? parseFloat(data.current.EMA).toFixed(2) : 'N/A';
                interpretation = indicator.interpretation(parseFloat(data.current.EMA), currentPrice);
              } else if (indicator.id === 'CCI') {
                value = data.current.CCI ? parseFloat(data.current.CCI).toFixed(1) : 'N/A';
                interpretation = indicator.interpretation(parseFloat(data.current.CCI));
              } else if (indicator.id === 'WILLR') {
                const willrVal = data.current.WILLR || data.current['WILLR'];
                value = willrVal ? parseFloat(willrVal).toFixed(1) : 'N/A';
                interpretation = indicator.interpretation(parseFloat(willrVal));
              } else if (indicator.id === 'MOM') {
                value = data.current.MOM ? parseFloat(data.current.MOM).toFixed(2) : 'N/A';
                interpretation = indicator.interpretation(parseFloat(data.current.MOM));
              } else if (indicator.id === 'ROC') {
                value = data.current.ROC ? parseFloat(data.current.ROC).toFixed(2) : 'N/A';
                interpretation = indicator.interpretation(parseFloat(data.current.ROC));
              } else if (indicator.id === 'MFI') {
                value = data.current.MFI ? parseFloat(data.current.MFI).toFixed(1) : 'N/A';
                interpretation = indicator.interpretation(parseFloat(data.current.MFI));
              } else if (indicator.id === 'AROON') {
                const aroonUp = data.current['Aroon Up'];
                value = aroonUp ? `Up: ${parseFloat(aroonUp).toFixed(0)}` : 'N/A';
                interpretation = indicator.interpretation(data.current);
              } else if (indicator.id === 'ATR') {
                value = data.current.ATR ? parseFloat(data.current.ATR).toFixed(2) : 'N/A';
                interpretation = indicator.interpretation(data.current.ATR);
              } else if (indicator.id === 'NATR') {
                value = data.current.NATR ? parseFloat(data.current.NATR).toFixed(2) + '%' : 'N/A';
                interpretation = indicator.interpretation(parseFloat(data.current.NATR));
              } else if (indicator.id === 'AD') {
                const adVal = data.current['Chaikin A/D'] || data.current.AD;
                value = adVal ? (parseFloat(adVal) / 1000000).toFixed(2) + 'M' : 'N/A';
                interpretation = indicator.interpretation(data.current, data.previous);
              } else if (indicator.id === 'ADOSC') {
                const adoscVal = data.current['Chaikin A/D Oscillator'] || data.current.ADOSC;
                value = adoscVal ? parseFloat(adoscVal).toFixed(0) : 'N/A';
                interpretation = indicator.interpretation(parseFloat(adoscVal));
              } else {
                value = 'N/A';
                interpretation = { signal: 'N/A', type: 'neutral', advice: '', color: '#64748b' };
              }

              const isExpanded = expandedIndicator === indicator.id;

              return (
                <div
                  key={indicator.id}
                  style={{
                    background: 'rgba(30, 41, 59, 0.8)',
                    border: `1px solid ${interpretation.color}40`,
                    borderRadius: '0.75rem',
                    overflow: 'hidden',
                    transition: 'all 0.2s'
                  }}
                >
                  {/* Indicator Header */}
                  <div style={{
                    padding: '1rem',
                    borderBottom: isExpanded ? '1px solid #334155' : 'none'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                        <div style={{
                          background: `${indicator.color}20`,
                          borderRadius: '0.5rem',
                          padding: '0.5rem',
                          color: indicator.color
                        }}>
                          {indicator.icon}
                        </div>
                        <div>
                          <h4 style={{ color: '#e2e8f0', margin: 0, fontSize: '0.95rem', fontWeight: '600' }}>
                            {indicator.name}
                          </h4>
                          <p style={{ color: '#94a3b8', margin: '0.25rem 0 0', fontSize: '0.8rem' }}>
                            {indicator.description}
                          </p>
                        </div>
                      </div>
                      
                      {/* Signal Badge */}
                      <div style={{
                        background: `${interpretation.color}20`,
                        color: interpretation.color,
                        padding: '0.35rem 0.75rem',
                        borderRadius: '0.5rem',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.35rem'
                      }}>
                        {getSignalIcon(interpretation.type)}
                        {interpretation.signal}
                      </div>
                    </div>

                    {/* Value & Advice */}
                    <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{
                        background: 'rgba(15, 23, 42, 0.6)',
                        padding: '0.5rem 1rem',
                        borderRadius: '0.5rem'
                      }}>
                        <span style={{ color: '#64748b', fontSize: '0.75rem' }}>Current Value: </span>
                        <span style={{ color: indicator.color, fontWeight: '700', fontSize: '1.1rem' }}>
                          {typeof value === 'number' ? value.toFixed(2) : value}
                        </span>
                      </div>
                      
                      <button
                        onClick={() => setExpandedIndicator(isExpanded ? null : indicator.id)}
                        style={{
                          background: 'rgba(100, 116, 139, 0.2)',
                          border: 'none',
                          borderRadius: '0.375rem',
                          padding: '0.5rem',
                          color: '#94a3b8',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          fontSize: '0.8rem'
                        }}
                      >
                        <HelpCircle size={14} />
                        {isExpanded ? 'Less' : 'Learn'}
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                    </div>

                    {/* Advice */}
                    <p style={{
                      color: '#cbd5e1',
                      margin: '0.75rem 0 0',
                      padding: '0.75rem',
                      background: `${interpretation.color}10`,
                      borderRadius: '0.5rem',
                      fontSize: '0.85rem',
                      borderLeft: `3px solid ${interpretation.color}`
                    }}>
                      {interpretation.advice}
                    </p>
                  </div>

                  {/* Expanded Education */}
                  {isExpanded && (
                    <div style={{
                      padding: '1rem',
                      background: 'rgba(15, 23, 42, 0.4)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <BookOpen size={16} color="#818cf8" />
                        <span style={{ color: '#a5b4fc', fontWeight: '600', fontSize: '0.85rem' }}>How to Read This Indicator</span>
                      </div>
                      <p style={{ color: '#94a3b8', margin: 0, fontSize: '0.85rem', lineHeight: '1.6' }}>
                        {indicator.howToRead}
                      </p>
                      
                      {/* Recent History */}
                      {data.history && data.history.length > 0 && (
                        <div style={{ marginTop: '1rem' }}>
                          <span style={{ color: '#64748b', fontSize: '0.8rem' }}>Recent Values:</span>
                          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                            {data.history.slice(0, 5).map((h, idx) => {
                              let histValue;
                              if (indicator.id === 'RSI') histValue = h.RSI;
                              else if (indicator.id === 'MACD') histValue = h.MACD;
                              else if (indicator.id === 'ADX') histValue = h.ADX;
                              else if (indicator.id === 'STOCH') histValue = h.SlowK;
                              else histValue = Object.values(h).find(v => typeof v === 'number');
                              
                              return (
                                <span key={idx} style={{
                                  background: 'rgba(51, 65, 85, 0.5)',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '0.25rem',
                                  fontSize: '0.75rem',
                                  color: idx === 0 ? indicator.color : '#94a3b8'
                                }}>
                                  {histValue ? parseFloat(histValue).toFixed(1) : 'N/A'}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Disclaimer */}
          <div style={{
            marginTop: '2rem',
            padding: '1.25rem',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '0.75rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
              <AlertCircle size={24} color="#f87171" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <h4 style={{ color: '#fca5a5', margin: '0 0 0.5rem', fontSize: '0.95rem', fontWeight: '700' }}>
                  ⚠️ FOR EDUCATIONAL PURPOSES ONLY
                </h4>
                <p style={{ color: '#fca5a5', margin: 0, fontSize: '0.85rem', lineHeight: '1.6' }}>
                  This technical analysis dashboard is provided <strong>strictly for educational and informational purposes only</strong>. 
                  The signals, indicators, and analysis shown here are <strong>NOT financial advice</strong> and should <strong>NOT</strong> be 
                  used as the sole basis for any investment decisions.
                </p>
                <p style={{ color: '#f87171', margin: '0.75rem 0 0', fontSize: '0.85rem', lineHeight: '1.6' }}>
                  <strong>Important:</strong> Technical indicators are historical tools and do not guarantee future performance. 
                  Always conduct your own research, consider your financial situation, and consult with a qualified financial advisor 
                  before making any investment decisions. Past performance is not indicative of future results. 
                  Trading stocks involves risk of loss.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Education Card Component
const EducationCard = ({ icon, title, description, color }) => (
  <div style={{
    background: 'rgba(30, 41, 59, 0.5)',
    border: '1px solid #334155',
    borderRadius: '0.75rem',
    padding: '1.25rem',
    borderLeft: `4px solid ${color}`
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
      {icon}
      <h4 style={{ color: '#e2e8f0', margin: 0, fontSize: '0.95rem', fontWeight: '600' }}>{title}</h4>
    </div>
    <p style={{ color: '#94a3b8', margin: 0, fontSize: '0.85rem', lineHeight: '1.5' }}>
      {description}
    </p>
  </div>
);

export default TechnicalAnalysisDashboard;